/**
 * Traducción entre los nombres que usa el HTML de un bloque y las tablas
 * de verdad.
 *
 * Vive aparte del componente que dibuja el bloque porque es donde se decide
 * que puede pedir un HTML y que no: sin fuente declarada no hay consulta.
 * Aquí no se toca la base, solo se arma lo que después se le pedira.
 */
import {
  findPerson,
  isOverlayField,
  isPeopleTable,
  MEMBER_FIELD,
  PEOPLE_ACCOUNT_FIELD,
} from "./people.ts";
import {
  type AppPerson,
  displayFieldOf,
  type FieldDef,
  type HtmlSource,
  isRelationField,
  orphanFieldName,
  SOURCE_BROKEN,
  type TableRecord,
} from "./types.ts";

/**
 * Cuántas filas trae como mucho una sola llamada a `plane.listar`.
 *
 * Vive aquí porque tiene dos lados y los dos tienen que decir lo mismo: el
 * servidor lo aplica (`runPageData` en `server/page/pageData.ts`) y el contrato se lo
 * cuenta al modelo (`shared/htmlContract.ts`). Cuando los dos números no
 * cuadran, una página cuenta el techo creyendo que cuenta la tabla y el número
 * corto parece el bueno.
 */
export const MAX_LIST_ROWS = 200;

/** Y cuantas trae cuando nadie pidió un tamaño. */
export const DEFAULT_LIST_ROWS = 30;

/**
 * Un fallo por culpa de lo que pidió el HTML, no de la base.
 *
 * `code` distingue el que viaja con su propio código hasta el navegador
 * (`SOURCE_BROKEN`) del que solo lleva mensaje.
 */
export class SourceError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Lo que se sabe de una columna de relación al servir una fila.
 *
 * `display` es la columna del destino que se enseña; `declared` son las columnas
 * de ese destino que alguna fuente de la página declaró, que son las únicas que
 * pueden salir del registro enlazado.
 */
interface ResolvedRelation {
  field: FieldDef;
  /** La tabla a la que apunta. Vacía si ya no existe. */
  target: TableRecord | null;
  /** Columna del destino que se enseña en lugar del id. */
  display: string;
  /** Nombre real de la columna del destino -> nombre con el que lo pide la página. */
  declared: Map<string, string>;
}

export interface Resolved {
  table: TableRecord;
  /** Nombre logico -> columna de verdad. */
  byLogical: Map<string, FieldDef>;
  /** Nombre logico -> lo que hace falta para resolver esa relación. */
  relations: Map<string, ResolvedRelation>;
}

/** Sufijo con el que una fila entrega el enlace de una celda de relación. */
export const LINK_SUFFIX = "_enlace";

/**
 * La forma vieja de una celda de relación, mientras dure la transición.
 *
 * Antes de este cambio una página recibía el id crudo en el nombre a secas.
 * Ahora ahí llega el valor que se ve, así que el id sale además por aquí para
 * que una página ya escrita que lo necesitara siga teniéndolo.
 *
 * **Cómo se sabe que ya no la pide nadie.** El id de un registro no se escribe
 * a mano en ninguna parte, así que una página solo puede nombrarlo por este
 * sufijo. Se retira cuando ningún documento de ninguna aplicación lo mencione:
 *
 *     grep -o '[a-zA-Z_][a-zA-Z0-9_]*_id' <documentos de las páginas>
 *
 * Hasta entonces se entrega siempre. Cuesta un campo por celda y es la única
 * pieza de este cambio que puede romper una página que ya funcionaba.
 */
const OLD_ID_SUFFIX = "_id";

/**
 * Busca la fuente en el manifiesto del bloque.
 * Una fuente que no esté declarada se rechaza aquí, antes de mirar nada.
 */
export function resolveSource(
  sources: HtmlSource[],
  tables: TableRecord[],
  name: unknown,
): Resolved {
  const wanted = String(name ?? "");
  const source = sources.find((s) => s.name === wanted);
  if (!source) throw new SourceError(`El bloque no declara la fuente "${wanted}".`, SOURCE_BROKEN);

  const table = tables.find((t) => t.id === source.tableId);
  if (!table) throw new SourceError(`La tabla de "${wanted}" ya no existe.`, SOURCE_BROKEN);

  const byLogical = new Map<string, FieldDef>();
  for (const [logical, id] of Object.entries(source.fields ?? {})) {
    const field = (table.fields ?? []).find((f) => f.id === id);
    if (field) byLogical.set(logical, field);
  }

  const relations = new Map<string, ResolvedRelation>();
  for (const [logical, field] of byLogical) {
    if (!isRelationField(field)) continue;
    const target = tables.find((t) => t.id === field.relationTableId) ?? null;
    relations.set(logical, {
      field,
      target,
      display: displayFieldOf(field) || firstTextField(target),
      declared: declaredOf(sources, field, target),
    });
  }

  return { table, byLogical, relations };
}

/**
 * Lo que la página declaró de la tabla a la que apunta una relación.
 *
 * De un registro enlazado solo puede salir lo que esté declarado, igual que
 * ocurre con las columnas de la tabla que se pide. Si no, la relación sería un
 * agujero por el que salen columnas que nadie declaró.
 *
 * Se mira en todas las fuentes de la página que apunten a esa tabla: declararla
 * una vez basta. La columna que enseña entra siempre, porque es lo que la propia
 * definición de la columna dice que se ve.
 */
function declaredOf(
  sources: HtmlSource[],
  field: FieldDef,
  target: TableRecord | null,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!target) return out;

  // Las dos columnas del sistema de la tabla de personas no están en ninguna
  // fuente --no se piden, se tienen-- y son las que dicen quien es alguien.
  // Entran siempre, como entra la columna que se enseña.
  if (isPeopleTable(target)) {
    out.set(PEOPLE_ACCOUNT_FIELD, PEOPLE_ACCOUNT_FIELD);
    out.set("email", "email");
    out.set("name", "name");
  }

  for (const source of sources) {
    if (source.tableId !== target.id) continue;
    for (const [logical, id] of Object.entries(source.fields ?? {})) {
      const column = (target.fields ?? []).find((f) => f.id === id);
      if (column) out.set(column.name, logical);
    }
  }

  const display = displayFieldOf(field) || firstTextField(target);
  if (display && !out.has(display)) out.set(display, display);
  return out;
}

/**
 * El respaldo de las columnas de relación de antes de este cambio, que no
 * declaran que enseñan: la primera columna de texto del destino, que es lo que
 * la cuadrícula venía adivinando. La migración lo deja escrito y esto deja de usarse.
 */
export function firstTextField(target: TableRecord | null): string {
  if (!target) return "";
  const found = (target.fields ?? []).find((f) => f.type === "text" || f.type === "email");
  return found?.name ?? "";
}

/** Las relaciones que hay que traerse resueltas al pedir filas. */
export function expandOf(resolved: Resolved): string {
  const names = [...resolved.relations.values()]
    .filter((rel) => rel.declared.size > 0)
    .map((rel) => rel.field.name);
  return names.join(",");
}

/**
 * Las columnas de una persona que no están en su fila.
 *
 * El correo vive en la cuenta y los roles en el enlace con la aplicación, así
 * que el expand de la base trae la fila sin ellos. Se superponen aquí, encima
 * de lo que la fila si trae. Ver `isOverlayField` en `shared/people.ts`.
 */
function personOverlay(person: AppPerson): Record<string, unknown> {
  return {
    [PEOPLE_ACCOUNT_FIELD]: person.email,
    email: person.email,
    name: person.name,
    roles: person.roles,
  };
}

/**
 * Una celda de relación tal como llega al documento.
 *
 * `valor` está siempre: con enlace es lo que enseña el registro, y sin enlace es
 * lo que se escribió y no encontró dueño. Por eso agrupar y contar se hace por
 * `valor` y nunca por `id`: agrupando por id las filas sin enlace se caerían de
 * las gráficas en silencio y el total no cuadraría con la tabla.
 */
interface LinkCell {
  /** Id del registro enlazado. Vacío: no hay enlace. */
  id: string;
  /** Hay id guardado pero el registro ya no existe o no está al alcance. */
  roto: boolean;
  /** Las columnas declaradas del registro enlazado. Vacío si no hay enlace. */
  registro: Record<string, unknown> | null;
}

/** La celda de relación resuelta: el valor que se ve y el enlace que hay detrás. */
function relationCell(
  rel: ResolvedRelation,
  record: Record<string, unknown>,
  people: AppPerson[],
): { valor: string; enlace: LinkCell } {
  const id = record[rel.field.name];

  if (!id) {
    // Sin enlace: el valor que se escribió sigue a la vista.
    const orphan = record[orphanFieldName(rel.field.name)];
    return {
      valor: orphan == null ? "" : String(orphan),
      enlace: { id: "", roto: false, registro: null },
    };
  }

  const expanded = (record.expand as Record<string, unknown> | undefined)?.[rel.field.name] as
    Record<string, unknown> | undefined;

  // La fila de una persona llega sin su correo ni sus roles: no están en su
  // colección. Se le superponen los de la lista de invitados, que el llamador
  // ya resolvió. Sin fila --porque quien mira no la alcanza-- la lista sola
  // basta para saber de quien se trata.
  const person = isPeopleTable(rel.target)
    ? findPerson(people, String(id), String(expanded?.[MEMBER_FIELD] ?? ""))
    : undefined;
  const linked =
    expanded || person
      ? { ...(expanded ?? {}), ...(person ? personOverlay(person) : {}) }
      : undefined;

  if (!linked) {
    // Hay id pero el registro no vino: se borró, ya no está invitada o quedó
    // fuera del alcance de quien mira. No se vacía en silencio; se dice que
    // está roto.
    return { valor: "", enlace: { id: String(id), roto: true, registro: null } };
  }

  const registro: Record<string, unknown> = {};
  for (const [real, logical] of rel.declared) registro[logical] = linked[real];
  const shown = linked[rel.display];

  return {
    valor: shown == null ? "" : String(shown),
    enlace: { id: String(id), roto: false, registro },
  };
}

/** Columna que corresponde a un nombre lógico declarado por la página. Falla si no la declaró. */
function columnOf(resolved: Resolved, logical: string): FieldDef {
  const field = resolved.byLogical.get(logical);
  if (!field) {
    throw new SourceError(`El bloque no declara la columna "${logical}".`, SOURCE_BROKEN);
  }
  return field;
}

/**
 * Una fila con los nombres que espera el HTML, no con los de la tabla.
 *
 * Una columna de relación llega como dos cosas: el nombre a secas es el valor
 * que se ve --texto, siempre presente, con enlace o sin el-- y el nombre con
 * `LINK_SUFFIX` es el enlace que hay detrás. Que debajo viva un id no aparece
 * en ninguna pantalla.
 */
/** Columnas que pone PocketBase sola y que un orden puede nombrar. */
const AUTO = new Set(["id", "created", "updated"]);

/**
 * Las fechas que pone la base en cada fila. Llegan a la página como fecha y
 * hora ISO en UTC: PocketBase las guarda con un espacio en vez de la "T", y
 * `new Date()` de Safari no lee esa forma.
 */
const STAMPS = ["created", "updated"] as const;

export function toLogicalRow(
  resolved: Resolved,
  record: Record<string, unknown>,
  people: AppPerson[] = [],
): Record<string, unknown> {
  const out: Record<string, unknown> = { id: record.id };
  for (const [logical, field] of resolved.byLogical) {
    const rel = resolved.relations.get(logical);
    if (!rel || field.multiple === true) {
      const value = record[field.name];
      // Una fecha se guarda con hora y zona ("2015-01-10 00:00:00.000Z") y el
      // contrato promete "yyyy-mm-dd": se entrega la fecha, que es lo que la
      // columna dice ser. Si no, la página la pinta tal cual.
      out[logical] =
        field.type === "date" && typeof value === "string" ? value.slice(0, 10) : value;
      continue;
    }
    const cell = relationCell(rel, record, people);
    out[logical] = cell.valor;
    out[`${logical}${LINK_SUFFIX}`] = cell.enlace;
    // La forma vieja, mientras dure la transición. Ver `OLD_ID_SUFFIX`.
    out[`${logical}${OLD_ID_SUFFIX}`] = cell.enlace.id;
  }
  for (const stamp of STAMPS) {
    const value = record[stamp];
    if (!(stamp in out) && typeof value === "string") out[stamp] = value.replace(" ", "T");
  }
  return out;
}

/**
 * Los valores que llegan del HTML, pasados a las columnas de verdad.
 *
 * En una relación lo que llega es la llave, no el id: el id nunca se escribe a
 * mano. Aquí solo se deja en el campo de reserva del valor sin dueño; quien guarda la
 * fila lo resuelve contra la tabla destino y decide cual de las dos columnas
 * reales se llena.
 */
export function toRealValues(resolved: Resolved, values: unknown): Record<string, unknown> {
  if (!values || typeof values !== "object") return {};
  const out: Record<string, unknown> = {};
  for (const [logical, value] of Object.entries(values as Record<string, unknown>)) {
    // Las pone la base: una fila leída y devuelta entera no las reescribe.
    if (AUTO.has(logical) && !resolved.byLogical.has(logical)) continue;
    // Los dos formatos del enlace son de lectura; una fila devuelta entera trae ambos.
    if (
      [LINK_SUFFIX, OLD_ID_SUFFIX].some(
        (suffix) =>
          logical.endsWith(suffix) &&
          resolved.relations.has(logical.slice(0, -suffix.length)) &&
          !resolved.byLogical.has(logical),
      )
    ) {
      continue;
    }
    const field = columnOf(resolved, logical);
    const rel = resolved.relations.get(logical);
    if (rel && field.multiple !== true) {
      out[orphanFieldName(field.name)] = value == null ? "" : String(value);
      continue;
    }
    out[field.name] = value;
  }
  return out;
}

/** Columnas donde tiene sentido buscar texto. */
const SEARCHABLE = ["text", "longtext", "email", "url", "select"];

/**
 * Por donde se alcanza, desde la fila que apunta, la columna que se enseña.
 *
 * Casi siempre es un salto: la relación y la columna del destino. El correo de
 * una persona son dos --la fila de personas no lo guarda, lo guarda su cuenta--
 * y los roles no son ninguno: viven en el enlace con la aplicación, que no
 * cuelga de la fila. Por esa última solo queda comparar contra el valor sin
 * dueño. Ver `isOverlayField` en `shared/people.ts`.
 */
const ACCOUNT_PATH: Record<string, string> = {
  [PEOPLE_ACCOUNT_FIELD]: PEOPLE_ACCOUNT_FIELD,
  // La forma vieja de nombrar el correo, que columnas de antes de este cambio
  // todavía guardan como lo que enseñan.
  email: PEOPLE_ACCOUNT_FIELD,
  name: "name",
};

/**
 * Ruta de consulta para mostrar el valor de una relación. En la tabla de personas, los datos de la
 * cuenta salen de otra tabla.
 */
function displayPath(rel: ResolvedRelation): string {
  const plain = `${rel.field.name}.${rel.display}`;
  // En cualquier otra tabla "nombre" o "correo" son columnas suyas y viven en
  // su colección: el salto de siempre.
  if (!isPeopleTable(rel.target)) return plain;
  if (!isOverlayField(rel.target, rel.display) && !(rel.display in ACCOUNT_PATH)) return plain;
  const column = ACCOUNT_PATH[rel.display];
  return column ? `${rel.field.name}.${MEMBER_FIELD}.${column}` : "";
}

/**
 * Comparar una relación por el valor que enseña, en sus dos columnas reales.
 *
 * Mirar solo la relación dejaría fuera las filas sin enlace, que son filas
 * válidas con un valor a la vista: buscar "CC123" tiene que encontrarlas.
 */
function relationMatch(rel: ResolvedRelation, op: string, literal: string): string {
  const orphan = `${orphanFieldName(rel.field.name)} ${op} ${literal}`;
  if (!rel.display) return `(${orphan})`;
  const path = displayPath(rel);
  if (!path) return `(${orphan})`;
  return `(${path} ${op} ${literal} || ${orphan})`;
}

/**
 * El filtro, armado a partir de lo que pidió el HTML.
 * El HTML nunca escribe el filtro: da columnas y valores, y se compone aquí.
 */
export function buildFilter(resolved: Resolved, opts: Record<string, unknown>): string {
  const clauses: string[] = [];

  const filtro = opts.filtro;
  if (filtro && typeof filtro === "object") {
    for (const [logical, value] of Object.entries(filtro as Record<string, unknown>)) {
      if ((STAMPS as readonly string[]).includes(logical) && !resolved.byLogical.has(logical)) {
        throw new SourceError(
          `No se puede filtrar por "${logical}": es una fecha con hora y el filtro compara valores exactos. Ordena por ella o compara las fechas de las filas.`,
        );
      }
      const field = columnOf(resolved, logical);
      const rel = resolved.relations.get(logical);
      if (rel && field.multiple !== true) {
        // Se filtra por el valor que se ve, en las dos columnas reales: si no,
        // las filas sin enlace nunca casarian con nada.
        clauses.push(relationMatch(rel, "=", JSON.stringify(String(value ?? ""))));
        continue;
      }
      clauses.push(`${field.name} = ${JSON.stringify(value ?? "")}`);
    }
  }

  const buscar = typeof opts.buscar === "string" ? opts.buscar.trim() : "";
  if (buscar) {
    const term = JSON.stringify(buscar);
    const parts = [...resolved.byLogical]
      .map(([logical, field]) => {
        const rel = resolved.relations.get(logical);
        if (rel && field.multiple !== true) return relationMatch(rel, "~", term);
        return SEARCHABLE.includes(field.type) ? `${field.name} ~ ${term}` : "";
      })
      .filter(Boolean);
    if (parts.length) clauses.push(`(${parts.join(" || ")})`);
  }

  return clauses.join(" && ");
}

/**
 * El orden con el que se piden las filas de una tabla.
 *
 * Sin orden dicho se enseña por antiguedad: la primera fila creada es la
 * primera que se ve, igual que en el editor de datos y en lo que se exporta.
 * La id desempata a las que nacieron en el mismo lote y comparten el instante.
 */
export function buildSort(resolved: Resolved, orden: unknown): string {
  if (typeof orden !== "string" || !orden.trim()) return "created,id";
  return orden
    .split(",")
    .map((part) => {
      const trimmed = part.trim();
      const sign = trimmed.startsWith("-") || trimmed.startsWith("+") ? trimmed[0] : "";
      const logical = sign ? trimmed.slice(1) : trimmed;
      if (AUTO.has(logical)) return `${sign}${logical}`;
      const field = columnOf(resolved, logical);
      const rel = resolved.relations.get(logical);
      // Ordenar por una relación ordena por el id, que no significa nada para
      // quien mira. Se ordena por el valor sin dueño, que es lo único que la
      // base sabe comparar aquí; el orden fino se hace en la página, que ya
      // tiene el valor de cada fila.
      if (rel && field.multiple !== true) return `${sign}${orphanFieldName(field.name)}`;
      return `${sign}${field.name}`;
    })
    .join(",");
}
